/**
 * Generated by orval v7.11.2 🍺
 * Do not edit manually.
 * UIBFS User Service API
 * API documentation for UIBFS User Service - handles authentication, user management, and profile operations
 * OpenAPI spec version: 1.0.0
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

import * as axios from "axios";
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";

import type {
  ActivateUser1Params,
  ApiResponse,
  AuthenticateUser200,
  ChangePasswordRequest,
  EnumPayload,
  GetAllUsersLegacyParams,
  GetAllUsersParams,
  GetRoleIdByName200,
  GetUsersForReportingParams,
  JwtAuthenticationResponse,
  LoginRequest,
  OrganisationMemberPayload,
  OrganisationPayload,
  Page,
  ResendInviteByEmailParams,
  Role,
  SearchUsersParams,
  SignUpRequest,
  UserDetailsRequest,
  UserDetailsResponse,
} from "./models";

// https://stackoverflow.com/questions/49579094/typescript-conditional-types-filter-out-readonly-properties-pick-only-requir/49579497#49579497
type IfEquals<X, Y, A = X, B = never> =
  (<T>() => T extends X ? 1 : 2) extends <T>() => T extends Y ? 1 : 2 ? A : B;

type WritableKeys<T> = {
  [P in keyof T]-?: IfEquals<
    { [Q in P]: T[P] },
    { -readonly [Q in P]: T[P] },
    P
  >;
}[keyof T];

type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (
  k: infer I,
) => void
  ? I
  : never;
type DistributeReadOnlyOverUnions<T> = T extends any ? NonReadonly<T> : never;

type Writable<T> = Pick<T, WritableKeys<T>>;
type NonReadonly<T> = [T] extends [UnionToIntersection<T>]
  ? {
      [P in keyof Writable<T>]: T[P] extends object
        ? NonReadonly<NonNullable<T[P]>>
        : T[P];
    }
  : DistributeReadOnlyOverUnions<T>;

/**
 * Updates core attributes of an existing organisation such as name, contacts or status.
 * @summary Update organisation
 */
export const updateOrganisation = (
  organisationPayload: OrganisationPayload,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<ApiResponse>> => {
  return axios.default.put(
    `https://membership.uibfs.or.ug/uib-api/user/organisation/update`,
    organisationPayload,
    options,
  );
};

export const getUpdateOrganisationMutationOptions = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateOrganisation>>,
    TError,
    { data: OrganisationPayload },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateOrganisation>>,
  TError,
  { data: OrganisationPayload },
  TContext
> => {
  const mutationKey = ["updateOrganisation"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateOrganisation>>,
    { data: OrganisationPayload }
  > = (props) => {
    const { data } = props ?? {};

    return updateOrganisation(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateOrganisationMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateOrganisation>>
>;
export type UpdateOrganisationMutationBody = OrganisationPayload;
export type UpdateOrganisationMutationError = AxiosError<unknown>;

/**
 * @summary Update organisation
 */
export const useUpdateOrganisation = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateOrganisation>>,
      TError,
      { data: OrganisationPayload },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateOrganisation>>,
  TError,
  { data: OrganisationPayload },
  TContext
> => {
  const mutationOptions = getUpdateOrganisationMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Marks the organisation as inactive and prevents member access until reactivated.
 * @summary Deactivate organisation
 */
export const deactivateOrganisation = (
  organisationId: number,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<ApiResponse>> => {
  return axios.default.put(
    `https://membership.uibfs.or.ug/uib-api/user/organisation/deactivate/${organisationId}`,
    undefined,
    options,
  );
};

export const getDeactivateOrganisationMutationOptions = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deactivateOrganisation>>,
    TError,
    { organisationId: number },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deactivateOrganisation>>,
  TError,
  { organisationId: number },
  TContext
> => {
  const mutationKey = ["deactivateOrganisation"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deactivateOrganisation>>,
    { organisationId: number }
  > = (props) => {
    const { organisationId } = props ?? {};

    return deactivateOrganisation(organisationId, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeactivateOrganisationMutationResult = NonNullable<
  Awaited<ReturnType<typeof deactivateOrganisation>>
>;

export type DeactivateOrganisationMutationError = AxiosError<unknown>;

/**
 * @summary Deactivate organisation
 */
export const useDeactivateOrganisation = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deactivateOrganisation>>,
      TError,
      { organisationId: number },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deactivateOrganisation>>,
  TError,
  { organisationId: number },
  TContext
> => {
  const mutationOptions = getDeactivateOrganisationMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Suspends a member within an organisation while retaining their historical associations.
 * @summary Deactivate organisation member
 */
export const deactivateOrganisationMember = (
  userId: number,
  organisationId: number,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<ApiResponse>> => {
  return axios.default.put(
    `https://membership.uibfs.or.ug/uib-api/user/organisation/deactivate-member/${userId}/${organisationId}`,
    undefined,
    options,
  );
};

export const getDeactivateOrganisationMemberMutationOptions = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deactivateOrganisationMember>>,
    TError,
    { userId: number; organisationId: number },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deactivateOrganisationMember>>,
  TError,
  { userId: number; organisationId: number },
  TContext
> => {
  const mutationKey = ["deactivateOrganisationMember"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deactivateOrganisationMember>>,
    { userId: number; organisationId: number }
  > = (props) => {
    const { userId, organisationId } = props ?? {};

    return deactivateOrganisationMember(userId, organisationId, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeactivateOrganisationMemberMutationResult = NonNullable<
  Awaited<ReturnType<typeof deactivateOrganisationMember>>
>;

export type DeactivateOrganisationMemberMutationError = AxiosError<unknown>;

/**
 * @summary Deactivate organisation member
 */
export const useDeactivateOrganisationMember = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deactivateOrganisationMember>>,
      TError,
      { userId: number; organisationId: number },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deactivateOrganisationMember>>,
  TError,
  { userId: number; organisationId: number },
  TContext
> => {
  const mutationOptions =
    getDeactivateOrganisationMemberMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Reactivates a previously deactivated organisation.
 * @summary Activate organisation
 */
export const activateOrganisation = (
  organisationId: number,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<ApiResponse>> => {
  return axios.default.put(
    `https://membership.uibfs.or.ug/uib-api/user/organisation/activate/${organisationId}`,
    undefined,
    options,
  );
};

export const getActivateOrganisationMutationOptions = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof activateOrganisation>>,
    TError,
    { organisationId: number },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof activateOrganisation>>,
  TError,
  { organisationId: number },
  TContext
> => {
  const mutationKey = ["activateOrganisation"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof activateOrganisation>>,
    { organisationId: number }
  > = (props) => {
    const { organisationId } = props ?? {};

    return activateOrganisation(organisationId, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ActivateOrganisationMutationResult = NonNullable<
  Awaited<ReturnType<typeof activateOrganisation>>
>;

export type ActivateOrganisationMutationError = AxiosError<unknown>;

/**
 * @summary Activate organisation
 */
export const useActivateOrganisation = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof activateOrganisation>>,
      TError,
      { organisationId: number },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof activateOrganisation>>,
  TError,
  { organisationId: number },
  TContext
> => {
  const mutationOptions = getActivateOrganisationMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Reactivates a member record previously deactivated within the organisation.
 * @summary Activate organisation member
 */
export const activateOrganisationMember = (
  userId: number,
  organisationId: number,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<ApiResponse>> => {
  return axios.default.put(
    `https://membership.uibfs.or.ug/uib-api/user/organisation/activate-member/${userId}/${organisationId}`,
    undefined,
    options,
  );
};

export const getActivateOrganisationMemberMutationOptions = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof activateOrganisationMember>>,
    TError,
    { userId: number; organisationId: number },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof activateOrganisationMember>>,
  TError,
  { userId: number; organisationId: number },
  TContext
> => {
  const mutationKey = ["activateOrganisationMember"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof activateOrganisationMember>>,
    { userId: number; organisationId: number }
  > = (props) => {
    const { userId, organisationId } = props ?? {};

    return activateOrganisationMember(userId, organisationId, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ActivateOrganisationMemberMutationResult = NonNullable<
  Awaited<ReturnType<typeof activateOrganisationMember>>
>;

export type ActivateOrganisationMemberMutationError = AxiosError<unknown>;

/**
 * @summary Activate organisation member
 */
export const useActivateOrganisationMember = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof activateOrganisationMember>>,
      TError,
      { userId: number; organisationId: number },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof activateOrganisationMember>>,
  TError,
  { userId: number; organisationId: number },
  TContext
> => {
  const mutationOptions = getActivateOrganisationMemberMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Replaces the user's current role with the role specified by the provided identifier.
 * @summary Assign role to user
 */
export const updateUserRole = (
  userId: number,
  roleId: number,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<ApiResponse>> => {
  return axios.default.post(
    `https://membership.uibfs.or.ug/uib-api/user/update-user-role/${userId}/${roleId}`,
    undefined,
    options,
  );
};

export const getUpdateUserRoleMutationOptions = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateUserRole>>,
    TError,
    { userId: number; roleId: number },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateUserRole>>,
  TError,
  { userId: number; roleId: number },
  TContext
> => {
  const mutationKey = ["updateUserRole"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateUserRole>>,
    { userId: number; roleId: number }
  > = (props) => {
    const { userId, roleId } = props ?? {};

    return updateUserRole(userId, roleId, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateUserRoleMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateUserRole>>
>;

export type UpdateUserRoleMutationError = AxiosError<unknown>;

/**
 * @summary Assign role to user
 */
export const useUpdateUserRole = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateUserRole>>,
      TError,
      { userId: number; roleId: number },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateUserRole>>,
  TError,
  { userId: number; roleId: number },
  TContext
> => {
  const mutationOptions = getUpdateUserRoleMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Updates profile attributes (names, contact information, password, etc.) for an existing user.
 * @summary Update user profile
 */
export const updateUserDetails = (
  signUpRequest: NonReadonly<SignUpRequest>,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<ApiResponse>> => {
  return axios.default.post(
    `https://membership.uibfs.or.ug/uib-api/user/update-user-details`,
    signUpRequest,
    options,
  );
};

export const getUpdateUserDetailsMutationOptions = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateUserDetails>>,
    TError,
    { data: NonReadonly<SignUpRequest> },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateUserDetails>>,
  TError,
  { data: NonReadonly<SignUpRequest> },
  TContext
> => {
  const mutationKey = ["updateUserDetails"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateUserDetails>>,
    { data: NonReadonly<SignUpRequest> }
  > = (props) => {
    const { data } = props ?? {};

    return updateUserDetails(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateUserDetailsMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateUserDetails>>
>;
export type UpdateUserDetailsMutationBody = NonReadonly<SignUpRequest>;
export type UpdateUserDetailsMutationError = AxiosError<unknown>;

/**
 * @summary Update user profile
 */
export const useUpdateUserDetails = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateUserDetails>>,
      TError,
      { data: NonReadonly<SignUpRequest> },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateUserDetails>>,
  TError,
  { data: NonReadonly<SignUpRequest> },
  TContext
> => {
  const mutationOptions = getUpdateUserDetailsMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Removes the specified role from the user's role assignments.
 * @summary Revoke role from user
 */
export const revokeRoleFromUser = (
  userId: number,
  roleId: number,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<ApiResponse>> => {
  return axios.default.post(
    `https://membership.uibfs.or.ug/uib-api/user/revoke-role-from-user/${userId}/${roleId}`,
    undefined,
    options,
  );
};

export const getRevokeRoleFromUserMutationOptions = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof revokeRoleFromUser>>,
    TError,
    { userId: number; roleId: number },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof revokeRoleFromUser>>,
  TError,
  { userId: number; roleId: number },
  TContext
> => {
  const mutationKey = ["revokeRoleFromUser"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof revokeRoleFromUser>>,
    { userId: number; roleId: number }
  > = (props) => {
    const { userId, roleId } = props ?? {};

    return revokeRoleFromUser(userId, roleId, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type RevokeRoleFromUserMutationResult = NonNullable<
  Awaited<ReturnType<typeof revokeRoleFromUser>>
>;

export type RevokeRoleFromUserMutationError = AxiosError<unknown>;

/**
 * @summary Revoke role from user
 */
export const useRevokeRoleFromUser = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof revokeRoleFromUser>>,
      TError,
      { userId: number; roleId: number },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof revokeRoleFromUser>>,
  TError,
  { userId: number; roleId: number },
  TContext
> => {
  const mutationOptions = getRevokeRoleFromUserMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Resends an organisation membership invitation to the specified user using their existing contact details.
 * @summary Resend member invite
 */
export const resendInvite = (
  userId: number,
  organisationId: number,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<ApiResponse>> => {
  return axios.default.post(
    `https://membership.uibfs.or.ug/uib-api/user/organisation/resend-invite/${userId}/${organisationId}`,
    undefined,
    options,
  );
};

export const getResendInviteMutationOptions = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof resendInvite>>,
    TError,
    { userId: number; organisationId: number },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof resendInvite>>,
  TError,
  { userId: number; organisationId: number },
  TContext
> => {
  const mutationKey = ["resendInvite"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof resendInvite>>,
    { userId: number; organisationId: number }
  > = (props) => {
    const { userId, organisationId } = props ?? {};

    return resendInvite(userId, organisationId, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ResendInviteMutationResult = NonNullable<
  Awaited<ReturnType<typeof resendInvite>>
>;

export type ResendInviteMutationError = AxiosError<unknown>;

/**
 * @summary Resend member invite
 */
export const useResendInvite = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof resendInvite>>,
      TError,
      { userId: number; organisationId: number },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof resendInvite>>,
  TError,
  { userId: number; organisationId: number },
  TContext
> => {
  const mutationOptions = getResendInviteMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Resends an organisation invitation to the specified email address, creating a provisional member when necessary.
 * @summary Resend invite by email
 */
export const resendInviteByEmail = (
  organisationId: number,
  params: ResendInviteByEmailParams,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<ApiResponse>> => {
  return axios.default.post(
    `https://membership.uibfs.or.ug/uib-api/user/organisation/resend-invite-by-email/${organisationId}`,
    undefined,
    {
      ...options,
      params: { ...params, ...options?.params },
    },
  );
};

export const getResendInviteByEmailMutationOptions = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof resendInviteByEmail>>,
    TError,
    { organisationId: number; params: ResendInviteByEmailParams },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof resendInviteByEmail>>,
  TError,
  { organisationId: number; params: ResendInviteByEmailParams },
  TContext
> => {
  const mutationKey = ["resendInviteByEmail"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof resendInviteByEmail>>,
    { organisationId: number; params: ResendInviteByEmailParams }
  > = (props) => {
    const { organisationId, params } = props ?? {};

    return resendInviteByEmail(organisationId, params, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ResendInviteByEmailMutationResult = NonNullable<
  Awaited<ReturnType<typeof resendInviteByEmail>>
>;

export type ResendInviteByEmailMutationError = AxiosError<unknown>;

/**
 * @summary Resend invite by email
 */
export const useResendInviteByEmail = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof resendInviteByEmail>>,
      TError,
      { organisationId: number; params: ResendInviteByEmailParams },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof resendInviteByEmail>>,
  TError,
  { organisationId: number; params: ResendInviteByEmailParams },
  TContext
> => {
  const mutationOptions = getResendInviteByEmailMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Creates a new organisation owned by the current user and optionally seeds members.
 * @summary Create organisation
 */
export const createOrganisation = (
  organisationPayload: OrganisationPayload,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<ApiResponse>> => {
  return axios.default.post(
    `https://membership.uibfs.or.ug/uib-api/user/organisation/create`,
    organisationPayload,
    options,
  );
};

export const getCreateOrganisationMutationOptions = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createOrganisation>>,
    TError,
    { data: OrganisationPayload },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createOrganisation>>,
  TError,
  { data: OrganisationPayload },
  TContext
> => {
  const mutationKey = ["createOrganisation"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createOrganisation>>,
    { data: OrganisationPayload }
  > = (props) => {
    const { data } = props ?? {};

    return createOrganisation(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateOrganisationMutationResult = NonNullable<
  Awaited<ReturnType<typeof createOrganisation>>
>;
export type CreateOrganisationMutationBody = OrganisationPayload;
export type CreateOrganisationMutationError = AxiosError<unknown>;

/**
 * @summary Create organisation
 */
export const useCreateOrganisation = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createOrganisation>>,
      TError,
      { data: OrganisationPayload },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createOrganisation>>,
  TError,
  { data: OrganisationPayload },
  TContext
> => {
  const mutationOptions = getCreateOrganisationMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Adds new members or invites existing users to the specified organisation.
 * @summary Add members to organisation
 */
export const addMember = (
  organisationPayload: OrganisationPayload,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<ApiResponse[]>> => {
  return axios.default.post(
    `https://membership.uibfs.or.ug/uib-api/user/organisation/add-member`,
    organisationPayload,
    options,
  );
};

export const getAddMemberMutationOptions = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof addMember>>,
    TError,
    { data: OrganisationPayload },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof addMember>>,
  TError,
  { data: OrganisationPayload },
  TContext
> => {
  const mutationKey = ["addMember"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof addMember>>,
    { data: OrganisationPayload }
  > = (props) => {
    const { data } = props ?? {};

    return addMember(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AddMemberMutationResult = NonNullable<
  Awaited<ReturnType<typeof addMember>>
>;
export type AddMemberMutationBody = OrganisationPayload;
export type AddMemberMutationError = AxiosError<unknown>;

/**
 * @summary Add members to organisation
 */
export const useAddMember = <TError = AxiosError<unknown>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof addMember>>,
      TError,
      { data: OrganisationPayload },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof addMember>>,
  TError,
  { data: OrganisationPayload },
  TContext
> => {
  const mutationOptions = getAddMemberMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Resolves a single user profile using the provided identifier, username/email or verification token.
 * @summary Fetch user by lookup payload
 */
export const getUserDetails = (
  userDetailsRequest: UserDetailsRequest,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<UserDetailsResponse>> => {
  return axios.default.post(
    `https://membership.uibfs.or.ug/uib-api/user/get-user-details`,
    userDetailsRequest,
    options,
  );
};

export const getGetUserDetailsMutationOptions = <
  TError = AxiosError<UserDetailsResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof getUserDetails>>,
    TError,
    { data: UserDetailsRequest },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof getUserDetails>>,
  TError,
  { data: UserDetailsRequest },
  TContext
> => {
  const mutationKey = ["getUserDetails"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof getUserDetails>>,
    { data: UserDetailsRequest }
  > = (props) => {
    const { data } = props ?? {};

    return getUserDetails(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type GetUserDetailsMutationResult = NonNullable<
  Awaited<ReturnType<typeof getUserDetails>>
>;
export type GetUserDetailsMutationBody = UserDetailsRequest;
export type GetUserDetailsMutationError = AxiosError<UserDetailsResponse>;

/**
 * @summary Fetch user by lookup payload
 */
export const useGetUserDetails = <
  TError = AxiosError<UserDetailsResponse>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof getUserDetails>>,
      TError,
      { data: UserDetailsRequest },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof getUserDetails>>,
  TError,
  { data: UserDetailsRequest },
  TContext
> => {
  const mutationOptions = getGetUserDetailsMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Returns the profile for the user identified by the supplied path variable.
 * @summary Fetch user by id
 */
export const getUserDetailsById = (
  id: number,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<UserDetailsResponse>> => {
  return axios.default.post(
    `https://membership.uibfs.or.ug/uib-api/user/get-user-details/${id}`,
    undefined,
    options,
  );
};

export const getGetUserDetailsByIdMutationOptions = <
  TError = AxiosError<UserDetailsResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof getUserDetailsById>>,
    TError,
    { id: number },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof getUserDetailsById>>,
  TError,
  { id: number },
  TContext
> => {
  const mutationKey = ["getUserDetailsById"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof getUserDetailsById>>,
    { id: number }
  > = (props) => {
    const { id } = props ?? {};

    return getUserDetailsById(id, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type GetUserDetailsByIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof getUserDetailsById>>
>;

export type GetUserDetailsByIdMutationError = AxiosError<UserDetailsResponse>;

/**
 * @summary Fetch user by id
 */
export const useGetUserDetailsById = <
  TError = AxiosError<UserDetailsResponse>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof getUserDetailsById>>,
      TError,
      { id: number },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof getUserDetailsById>>,
  TError,
  { id: number },
  TContext
> => {
  const mutationOptions = getGetUserDetailsByIdMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

export const getRoleIdByName = (
  roleName: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<GetRoleIdByName200>> => {
  return axios.default.post(
    `https://membership.uibfs.or.ug/uib-api/user/get-role-id/${roleName}`,
    undefined,
    options,
  );
};

export const getGetRoleIdByNameMutationOptions = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof getRoleIdByName>>,
    TError,
    { roleName: string },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof getRoleIdByName>>,
  TError,
  { roleName: string },
  TContext
> => {
  const mutationKey = ["getRoleIdByName"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof getRoleIdByName>>,
    { roleName: string }
  > = (props) => {
    const { roleName } = props ?? {};

    return getRoleIdByName(roleName, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type GetRoleIdByNameMutationResult = NonNullable<
  Awaited<ReturnType<typeof getRoleIdByName>>
>;

export type GetRoleIdByNameMutationError = AxiosError<unknown>;

export const useGetRoleIdByName = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof getRoleIdByName>>,
      TError,
      { roleName: string },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof getRoleIdByName>>,
  TError,
  { roleName: string },
  TContext
> => {
  const mutationOptions = getGetRoleIdByNameMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Returns profile details for all supplied user ids in a single request.
 * @summary Get profiles for multiple users
 */
export const getAllUserDetails = (
  getAllUserDetailsBody: number[],
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<UserDetailsResponse[]>> => {
  return axios.default.post(
    `https://membership.uibfs.or.ug/uib-api/user/get-all-user-details`,
    getAllUserDetailsBody,
    options,
  );
};

export const getGetAllUserDetailsMutationOptions = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof getAllUserDetails>>,
    TError,
    { data: number[] },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof getAllUserDetails>>,
  TError,
  { data: number[] },
  TContext
> => {
  const mutationKey = ["getAllUserDetails"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof getAllUserDetails>>,
    { data: number[] }
  > = (props) => {
    const { data } = props ?? {};

    return getAllUserDetails(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type GetAllUserDetailsMutationResult = NonNullable<
  Awaited<ReturnType<typeof getAllUserDetails>>
>;
export type GetAllUserDetailsMutationBody = number[];
export type GetAllUserDetailsMutationError = AxiosError<unknown>;

/**
 * @summary Get profiles for multiple users
 */
export const useGetAllUserDetails = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof getAllUserDetails>>,
      TError,
      { data: number[] },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof getAllUserDetails>>,
  TError,
  { data: number[] },
  TContext
> => {
  const mutationOptions = getGetAllUserDetailsMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Marks the selected user account as deactivated, preventing access until reactivated.
 * @summary Deactivate user
 */
export const deactivateUser = (
  userId: number,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<ApiResponse>> => {
  return axios.default.post(
    `https://membership.uibfs.or.ug/uib-api/user/deactivate-user/${userId}`,
    undefined,
    options,
  );
};

export const getDeactivateUserMutationOptions = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deactivateUser>>,
    TError,
    { userId: number },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deactivateUser>>,
  TError,
  { userId: number },
  TContext
> => {
  const mutationKey = ["deactivateUser"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deactivateUser>>,
    { userId: number }
  > = (props) => {
    const { userId } = props ?? {};

    return deactivateUser(userId, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeactivateUserMutationResult = NonNullable<
  Awaited<ReturnType<typeof deactivateUser>>
>;

export type DeactivateUserMutationError = AxiosError<unknown>;

/**
 * @summary Deactivate user
 */
export const useDeactivateUser = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deactivateUser>>,
      TError,
      { userId: number },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deactivateUser>>,
  TError,
  { userId: number },
  TContext
> => {
  const mutationOptions = getDeactivateUserMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Allows the authenticated user to change their password using the current password.
 * @summary Change password
 */
export const changePassword = (
  changePasswordRequest: ChangePasswordRequest,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<ApiResponse>> => {
  return axios.default.post(
    `https://membership.uibfs.or.ug/uib-api/user/change-password`,
    changePasswordRequest,
    options,
  );
};

export const getChangePasswordMutationOptions = <
  TError = AxiosError<ApiResponse | ApiResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof changePassword>>,
    TError,
    { data: ChangePasswordRequest },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof changePassword>>,
  TError,
  { data: ChangePasswordRequest },
  TContext
> => {
  const mutationKey = ["changePassword"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof changePassword>>,
    { data: ChangePasswordRequest }
  > = (props) => {
    const { data } = props ?? {};

    return changePassword(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ChangePasswordMutationResult = NonNullable<
  Awaited<ReturnType<typeof changePassword>>
>;
export type ChangePasswordMutationBody = ChangePasswordRequest;
export type ChangePasswordMutationError = AxiosError<ApiResponse | ApiResponse>;

/**
 * @summary Change password
 */
export const useChangePassword = <
  TError = AxiosError<ApiResponse | ApiResponse>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof changePassword>>,
      TError,
      { data: ChangePasswordRequest },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof changePassword>>,
  TError,
  { data: ChangePasswordRequest },
  TContext
> => {
  const mutationOptions = getChangePasswordMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Assigns a department to multiple users in one request.
 * @summary Add users to department (bulk)
 */
export const addUsersToDepartment = (
  departmentId: number,
  addUsersToDepartmentBody: number[],
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<ApiResponse[]>> => {
  return axios.default.post(
    `https://membership.uibfs.or.ug/uib-api/user/add-users-to-department/${departmentId}`,
    addUsersToDepartmentBody,
    options,
  );
};

export const getAddUsersToDepartmentMutationOptions = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof addUsersToDepartment>>,
    TError,
    { departmentId: number; data: number[] },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof addUsersToDepartment>>,
  TError,
  { departmentId: number; data: number[] },
  TContext
> => {
  const mutationKey = ["addUsersToDepartment"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof addUsersToDepartment>>,
    { departmentId: number; data: number[] }
  > = (props) => {
    const { departmentId, data } = props ?? {};

    return addUsersToDepartment(departmentId, data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AddUsersToDepartmentMutationResult = NonNullable<
  Awaited<ReturnType<typeof addUsersToDepartment>>
>;
export type AddUsersToDepartmentMutationBody = number[];
export type AddUsersToDepartmentMutationError = AxiosError<unknown>;

/**
 * @summary Add users to department (bulk)
 */
export const useAddUsersToDepartment = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof addUsersToDepartment>>,
      TError,
      { departmentId: number; data: number[] },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof addUsersToDepartment>>,
  TError,
  { departmentId: number; data: number[] },
  TContext
> => {
  const mutationOptions = getAddUsersToDepartmentMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Assigns the specified department to a user.
 * @summary Add user to department
 */
export const addUserToDepartment = (
  userId: number,
  departmentId: number,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<ApiResponse>> => {
  return axios.default.post(
    `https://membership.uibfs.or.ug/uib-api/user/add-user-to-department/${userId}/${departmentId}`,
    undefined,
    options,
  );
};

export const getAddUserToDepartmentMutationOptions = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof addUserToDepartment>>,
    TError,
    { userId: number; departmentId: number },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof addUserToDepartment>>,
  TError,
  { userId: number; departmentId: number },
  TContext
> => {
  const mutationKey = ["addUserToDepartment"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof addUserToDepartment>>,
    { userId: number; departmentId: number }
  > = (props) => {
    const { userId, departmentId } = props ?? {};

    return addUserToDepartment(userId, departmentId, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AddUserToDepartmentMutationResult = NonNullable<
  Awaited<ReturnType<typeof addUserToDepartment>>
>;

export type AddUserToDepartmentMutationError = AxiosError<unknown>;

/**
 * @summary Add user to department
 */
export const useAddUserToDepartment = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof addUserToDepartment>>,
      TError,
      { userId: number; departmentId: number },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof addUserToDepartment>>,
  TError,
  { userId: number; departmentId: number },
  TContext
> => {
  const mutationOptions = getAddUserToDepartmentMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

export const addRoleToUser = (
  userId: number,
  roleId: number,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<ApiResponse>> => {
  return axios.default.post(
    `https://membership.uibfs.or.ug/uib-api/user/add-role/${userId}/${roleId}`,
    undefined,
    options,
  );
};

export const getAddRoleToUserMutationOptions = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof addRoleToUser>>,
    TError,
    { userId: number; roleId: number },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof addRoleToUser>>,
  TError,
  { userId: number; roleId: number },
  TContext
> => {
  const mutationKey = ["addRoleToUser"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof addRoleToUser>>,
    { userId: number; roleId: number }
  > = (props) => {
    const { userId, roleId } = props ?? {};

    return addRoleToUser(userId, roleId, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AddRoleToUserMutationResult = NonNullable<
  Awaited<ReturnType<typeof addRoleToUser>>
>;

export type AddRoleToUserMutationError = AxiosError<unknown>;

export const useAddRoleToUser = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof addRoleToUser>>,
      TError,
      { userId: number; roleId: number },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof addRoleToUser>>,
  TError,
  { userId: number; roleId: number },
  TContext
> => {
  const mutationOptions = getAddRoleToUserMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Links a user with the specified organisation, granting the relevant membership role.
 * @summary Add user to organisation
 */
export const addUserToOrganisation = (
  userId: number,
  organisationId: number,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<ApiResponse>> => {
  return axios.default.post(
    `https://membership.uibfs.or.ug/uib-api/user/add-User-to-organisation/${userId}/${organisationId}`,
    undefined,
    options,
  );
};

export const getAddUserToOrganisationMutationOptions = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof addUserToOrganisation>>,
    TError,
    { userId: number; organisationId: number },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof addUserToOrganisation>>,
  TError,
  { userId: number; organisationId: number },
  TContext
> => {
  const mutationKey = ["addUserToOrganisation"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof addUserToOrganisation>>,
    { userId: number; organisationId: number }
  > = (props) => {
    const { userId, organisationId } = props ?? {};

    return addUserToOrganisation(userId, organisationId, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AddUserToOrganisationMutationResult = NonNullable<
  Awaited<ReturnType<typeof addUserToOrganisation>>
>;

export type AddUserToOrganisationMutationError = AxiosError<unknown>;

/**
 * @summary Add user to organisation
 */
export const useAddUserToOrganisation = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof addUserToOrganisation>>,
      TError,
      { userId: number; organisationId: number },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof addUserToOrganisation>>,
  TError,
  { userId: number; organisationId: number },
  TContext
> => {
  const mutationOptions = getAddUserToOrganisationMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Reactivates a previously deactivated user account.
 * @summary Activate user
 */
export const activateUser = (
  userId: number,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<ApiResponse>> => {
  return axios.default.post(
    `https://membership.uibfs.or.ug/uib-api/user/activate-user/${userId}`,
    undefined,
    options,
  );
};

export const getActivateUserMutationOptions = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof activateUser>>,
    TError,
    { userId: number },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof activateUser>>,
  TError,
  { userId: number },
  TContext
> => {
  const mutationKey = ["activateUser"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof activateUser>>,
    { userId: number }
  > = (props) => {
    const { userId } = props ?? {};

    return activateUser(userId, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ActivateUserMutationResult = NonNullable<
  Awaited<ReturnType<typeof activateUser>>
>;

export type ActivateUserMutationError = AxiosError<unknown>;

/**
 * @summary Activate user
 */
export const useActivateUser = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof activateUser>>,
      TError,
      { userId: number },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof activateUser>>,
  TError,
  { userId: number },
  TContext
> => {
  const mutationOptions = getActivateUserMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Creates a new user account, generates activation token and dispatches onboarding notifications.
 * @summary Register a new account
 */
export const registerUser = (
  signUpRequest: NonReadonly<SignUpRequest>,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<ApiResponse>> => {
  return axios.default.post(
    `https://membership.uibfs.or.ug/uib-api/auth/sign-up`,
    signUpRequest,
    options,
  );
};

export const getRegisterUserMutationOptions = <
  TError = AxiosError<ApiResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof registerUser>>,
    TError,
    { data: NonReadonly<SignUpRequest> },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof registerUser>>,
  TError,
  { data: NonReadonly<SignUpRequest> },
  TContext
> => {
  const mutationKey = ["registerUser"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof registerUser>>,
    { data: NonReadonly<SignUpRequest> }
  > = (props) => {
    const { data } = props ?? {};

    return registerUser(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type RegisterUserMutationResult = NonNullable<
  Awaited<ReturnType<typeof registerUser>>
>;
export type RegisterUserMutationBody = NonReadonly<SignUpRequest>;
export type RegisterUserMutationError = AxiosError<ApiResponse>;

/**
 * @summary Register a new account
 */
export const useRegisterUser = <
  TError = AxiosError<ApiResponse>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof registerUser>>,
      TError,
      { data: NonReadonly<SignUpRequest> },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof registerUser>>,
  TError,
  { data: NonReadonly<SignUpRequest> },
  TContext
> => {
  const mutationOptions = getRegisterUserMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Validates the provided username/email and password and issues fresh JWT tokens when successful.
 * @summary Authenticate with credentials
 */
export const authenticateUser = (
  loginRequest: NonReadonly<LoginRequest>,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<AuthenticateUser200>> => {
  return axios.default.post(
    `https://membership.uibfs.or.ug/uib-api/auth/sign-in`,
    loginRequest,
    options,
  );
};

export const getAuthenticateUserMutationOptions = <
  TError = AxiosError<ApiResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof authenticateUser>>,
    TError,
    { data: NonReadonly<LoginRequest> },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof authenticateUser>>,
  TError,
  { data: NonReadonly<LoginRequest> },
  TContext
> => {
  const mutationKey = ["authenticateUser"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof authenticateUser>>,
    { data: NonReadonly<LoginRequest> }
  > = (props) => {
    const { data } = props ?? {};

    return authenticateUser(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AuthenticateUserMutationResult = NonNullable<
  Awaited<ReturnType<typeof authenticateUser>>
>;
export type AuthenticateUserMutationBody = NonReadonly<LoginRequest>;
export type AuthenticateUserMutationError = AxiosError<ApiResponse>;

/**
 * @summary Authenticate with credentials
 */
export const useAuthenticateUser = <
  TError = AxiosError<ApiResponse>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof authenticateUser>>,
      TError,
      { data: NonReadonly<LoginRequest> },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof authenticateUser>>,
  TError,
  { data: NonReadonly<LoginRequest> },
  TContext
> => {
  const mutationOptions = getAuthenticateUserMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Generates a password reset token and sends it to the user via configured notification channels.
 * @summary Request password reset
 */
export const forgotPassword = (
  signUpRequest: NonReadonly<SignUpRequest>,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<ApiResponse>> => {
  return axios.default.post(
    `https://membership.uibfs.or.ug/uib-api/auth/request-password-reset`,
    signUpRequest,
    options,
  );
};

export const getForgotPasswordMutationOptions = <
  TError = AxiosError<ApiResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof forgotPassword>>,
    TError,
    { data: NonReadonly<SignUpRequest> },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof forgotPassword>>,
  TError,
  { data: NonReadonly<SignUpRequest> },
  TContext
> => {
  const mutationKey = ["forgotPassword"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof forgotPassword>>,
    { data: NonReadonly<SignUpRequest> }
  > = (props) => {
    const { data } = props ?? {};

    return forgotPassword(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ForgotPasswordMutationResult = NonNullable<
  Awaited<ReturnType<typeof forgotPassword>>
>;
export type ForgotPasswordMutationBody = NonReadonly<SignUpRequest>;
export type ForgotPasswordMutationError = AxiosError<ApiResponse>;

/**
 * @summary Request password reset
 */
export const useForgotPassword = <
  TError = AxiosError<ApiResponse>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof forgotPassword>>,
      TError,
      { data: NonReadonly<SignUpRequest> },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof forgotPassword>>,
  TError,
  { data: NonReadonly<SignUpRequest> },
  TContext
> => {
  const mutationOptions = getForgotPasswordMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Uses the refresh token cookie to issue a new access token when the previous one has expired.
 * @summary Refresh JWT access token
 */
export const refreshToken = (
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<JwtAuthenticationResponse>> => {
  return axios.default.post(
    `https://membership.uibfs.or.ug/uib-api/auth/refresh`,
    undefined,
    options,
  );
};

export const getRefreshTokenMutationOptions = <
  TError = AxiosError<ApiResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof refreshToken>>,
    TError,
    void,
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof refreshToken>>,
  TError,
  void,
  TContext
> => {
  const mutationKey = ["refreshToken"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof refreshToken>>,
    void
  > = () => {
    return refreshToken(axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type RefreshTokenMutationResult = NonNullable<
  Awaited<ReturnType<typeof refreshToken>>
>;

export type RefreshTokenMutationError = AxiosError<ApiResponse>;

/**
 * @summary Refresh JWT access token
 */
export const useRefreshToken = <
  TError = AxiosError<ApiResponse>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof refreshToken>>,
      TError,
      void,
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof refreshToken>>,
  TError,
  void,
  TContext
> => {
  const mutationOptions = getRefreshTokenMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Updates the account password using a valid reset token and notifies the user.
 * @summary Reset password
 */
export const passwordReset = (
  signUpRequest: NonReadonly<SignUpRequest>,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<ApiResponse>> => {
  return axios.default.post(
    `https://membership.uibfs.or.ug/uib-api/auth/password-reset`,
    signUpRequest,
    options,
  );
};

export const getPasswordResetMutationOptions = <
  TError = AxiosError<ApiResponse | ApiResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof passwordReset>>,
    TError,
    { data: NonReadonly<SignUpRequest> },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof passwordReset>>,
  TError,
  { data: NonReadonly<SignUpRequest> },
  TContext
> => {
  const mutationKey = ["passwordReset"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof passwordReset>>,
    { data: NonReadonly<SignUpRequest> }
  > = (props) => {
    const { data } = props ?? {};

    return passwordReset(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PasswordResetMutationResult = NonNullable<
  Awaited<ReturnType<typeof passwordReset>>
>;
export type PasswordResetMutationBody = NonReadonly<SignUpRequest>;
export type PasswordResetMutationError = AxiosError<ApiResponse | ApiResponse>;

/**
 * @summary Reset password
 */
export const usePasswordReset = <
  TError = AxiosError<ApiResponse | ApiResponse>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof passwordReset>>,
      TError,
      { data: NonReadonly<SignUpRequest> },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof passwordReset>>,
  TError,
  { data: NonReadonly<SignUpRequest> },
  TContext
> => {
  const mutationOptions = getPasswordResetMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Generates a one-time password, updates the account, and emails it to the user. Intended for internal system workflows.
 * @summary Issue a temporary password for an existing user
 */
export const issueTemporaryPassword = (
  userId: number,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<ApiResponse>> => {
  return axios.default.post(
    `https://membership.uibfs.or.ug/uib-api/auth/issue-temp-password/${userId}`,
    undefined,
    options,
  );
};

export const getIssueTemporaryPasswordMutationOptions = <
  TError = AxiosError<ApiResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof issueTemporaryPassword>>,
    TError,
    { userId: number },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof issueTemporaryPassword>>,
  TError,
  { userId: number },
  TContext
> => {
  const mutationKey = ["issueTemporaryPassword"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof issueTemporaryPassword>>,
    { userId: number }
  > = (props) => {
    const { userId } = props ?? {};

    return issueTemporaryPassword(userId, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type IssueTemporaryPasswordMutationResult = NonNullable<
  Awaited<ReturnType<typeof issueTemporaryPassword>>
>;

export type IssueTemporaryPasswordMutationError = AxiosError<ApiResponse>;

/**
 * @summary Issue a temporary password for an existing user
 */
export const useIssueTemporaryPassword = <
  TError = AxiosError<ApiResponse>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof issueTemporaryPassword>>,
      TError,
      { userId: number },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof issueTemporaryPassword>>,
  TError,
  { userId: number },
  TContext
> => {
  const mutationOptions = getIssueTemporaryPasswordMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Returns detailed profile information for the supplied numeric identifiers.
 * @summary Resolve details for multiple users
 */
export const getAllUserDetails1 = (
  getAllUserDetails1Body: number[],
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<UserDetailsResponse[]>> => {
  return axios.default.post(
    `https://membership.uibfs.or.ug/uib-api/auth/get-all-user-details`,
    getAllUserDetails1Body,
    options,
  );
};

export const getGetAllUserDetails1MutationOptions = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof getAllUserDetails1>>,
    TError,
    { data: number[] },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof getAllUserDetails1>>,
  TError,
  { data: number[] },
  TContext
> => {
  const mutationKey = ["getAllUserDetails1"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof getAllUserDetails1>>,
    { data: number[] }
  > = (props) => {
    const { data } = props ?? {};

    return getAllUserDetails1(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type GetAllUserDetails1MutationResult = NonNullable<
  Awaited<ReturnType<typeof getAllUserDetails1>>
>;
export type GetAllUserDetails1MutationBody = number[];
export type GetAllUserDetails1MutationError = AxiosError<unknown>;

/**
 * @summary Resolve details for multiple users
 */
export const useGetAllUserDetails1 = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof getAllUserDetails1>>,
      TError,
      { data: number[] },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof getAllUserDetails1>>,
  TError,
  { data: number[] },
  TContext
> => {
  const mutationOptions = getGetAllUserDetails1MutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

export const createAccountWithGeneratedPassword = (
  signUpRequest: NonReadonly<SignUpRequest>,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<ApiResponse>> => {
  return axios.default.post(
    `https://membership.uibfs.or.ug/uib-api/auth/create-account-with-password`,
    signUpRequest,
    options,
  );
};

export const getCreateAccountWithGeneratedPasswordMutationOptions = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createAccountWithGeneratedPassword>>,
    TError,
    { data: NonReadonly<SignUpRequest> },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createAccountWithGeneratedPassword>>,
  TError,
  { data: NonReadonly<SignUpRequest> },
  TContext
> => {
  const mutationKey = ["createAccountWithGeneratedPassword"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createAccountWithGeneratedPassword>>,
    { data: NonReadonly<SignUpRequest> }
  > = (props) => {
    const { data } = props ?? {};

    return createAccountWithGeneratedPassword(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateAccountWithGeneratedPasswordMutationResult = NonNullable<
  Awaited<ReturnType<typeof createAccountWithGeneratedPassword>>
>;
export type CreateAccountWithGeneratedPasswordMutationBody =
  NonReadonly<SignUpRequest>;
export type CreateAccountWithGeneratedPasswordMutationError =
  AxiosError<unknown>;

export const useCreateAccountWithGeneratedPassword = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createAccountWithGeneratedPassword>>,
      TError,
      { data: NonReadonly<SignUpRequest> },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createAccountWithGeneratedPassword>>,
  TError,
  { data: NonReadonly<SignUpRequest> },
  TContext
> => {
  const mutationOptions =
    getCreateAccountWithGeneratedPasswordMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Performs a paginated search by email or name with optional status filtering.
 * @summary Search users
 */
export const searchUsers = (
  params: SearchUsersParams,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<Page>> => {
  return axios.default.get(
    `https://membership.uibfs.or.ug/uib-api/user/search-users`,
    {
      ...options,
      params: { ...params, ...options?.params },
    },
  );
};

export const getSearchUsersQueryKey = (params?: SearchUsersParams) => {
  return [
    `https://membership.uibfs.or.ug/uib-api/user/search-users`,
    ...(params ? [params] : []),
  ] as const;
};

export const getSearchUsersQueryOptions = <
  TData = Awaited<ReturnType<typeof searchUsers>>,
  TError = AxiosError<unknown>,
>(
  params: SearchUsersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof searchUsers>>, TError, TData>
    >;
    axios?: AxiosRequestConfig;
  },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getSearchUsersQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof searchUsers>>> = ({
    signal,
  }) => searchUsers(params, { signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof searchUsers>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type SearchUsersQueryResult = NonNullable<
  Awaited<ReturnType<typeof searchUsers>>
>;
export type SearchUsersQueryError = AxiosError<unknown>;

export function useSearchUsers<
  TData = Awaited<ReturnType<typeof searchUsers>>,
  TError = AxiosError<unknown>,
>(
  params: SearchUsersParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof searchUsers>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof searchUsers>>,
          TError,
          Awaited<ReturnType<typeof searchUsers>>
        >,
        "initialData"
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useSearchUsers<
  TData = Awaited<ReturnType<typeof searchUsers>>,
  TError = AxiosError<unknown>,
>(
  params: SearchUsersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof searchUsers>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof searchUsers>>,
          TError,
          Awaited<ReturnType<typeof searchUsers>>
        >,
        "initialData"
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useSearchUsers<
  TData = Awaited<ReturnType<typeof searchUsers>>,
  TError = AxiosError<unknown>,
>(
  params: SearchUsersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof searchUsers>>, TError, TData>
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Search users
 */

export function useSearchUsers<
  TData = Awaited<ReturnType<typeof searchUsers>>,
  TError = AxiosError<unknown>,
>(
  params: SearchUsersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof searchUsers>>, TError, TData>
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getSearchUsersQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Returns users scoped by organisation or department for reporting dashboards.
 * @summary List users for reporting
 */
export const getUsersForReporting = (
  params?: GetUsersForReportingParams,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<UserDetailsResponse[]>> => {
  return axios.default.get(
    `https://membership.uibfs.or.ug/uib-api/user/reporting/users`,
    {
      ...options,
      params: { ...params, ...options?.params },
    },
  );
};

export const getGetUsersForReportingQueryKey = (
  params?: GetUsersForReportingParams,
) => {
  return [
    `https://membership.uibfs.or.ug/uib-api/user/reporting/users`,
    ...(params ? [params] : []),
  ] as const;
};

export const getGetUsersForReportingQueryOptions = <
  TData = Awaited<ReturnType<typeof getUsersForReporting>>,
  TError = AxiosError<unknown>,
>(
  params?: GetUsersForReportingParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getUsersForReporting>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetUsersForReportingQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getUsersForReporting>>
  > = ({ signal }) => getUsersForReporting(params, { signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getUsersForReporting>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetUsersForReportingQueryResult = NonNullable<
  Awaited<ReturnType<typeof getUsersForReporting>>
>;
export type GetUsersForReportingQueryError = AxiosError<unknown>;

export function useGetUsersForReporting<
  TData = Awaited<ReturnType<typeof getUsersForReporting>>,
  TError = AxiosError<unknown>,
>(
  params: undefined | GetUsersForReportingParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getUsersForReporting>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUsersForReporting>>,
          TError,
          Awaited<ReturnType<typeof getUsersForReporting>>
        >,
        "initialData"
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetUsersForReporting<
  TData = Awaited<ReturnType<typeof getUsersForReporting>>,
  TError = AxiosError<unknown>,
>(
  params?: GetUsersForReportingParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getUsersForReporting>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUsersForReporting>>,
          TError,
          Awaited<ReturnType<typeof getUsersForReporting>>
        >,
        "initialData"
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetUsersForReporting<
  TData = Awaited<ReturnType<typeof getUsersForReporting>>,
  TError = AxiosError<unknown>,
>(
  params?: GetUsersForReportingParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getUsersForReporting>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List users for reporting
 */

export function useGetUsersForReporting<
  TData = Awaited<ReturnType<typeof getUsersForReporting>>,
  TError = AxiosError<unknown>,
>(
  params?: GetUsersForReportingParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getUsersForReporting>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetUsersForReportingQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Fetches an organisation along with metadata and membership summary.
 * @summary Get organisation by id
 */
export const getOrganisation = (
  id: number,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<OrganisationPayload>> => {
  return axios.default.get(
    `https://membership.uibfs.or.ug/uib-api/user/organisation/get/${id}`,
    options,
  );
};

export const getGetOrganisationQueryKey = (id?: number) => {
  return [
    `https://membership.uibfs.or.ug/uib-api/user/organisation/get/${id}`,
  ] as const;
};

export const getGetOrganisationQueryOptions = <
  TData = Awaited<ReturnType<typeof getOrganisation>>,
  TError = AxiosError<OrganisationPayload>,
>(
  id: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOrganisation>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetOrganisationQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getOrganisation>>> = ({
    signal,
  }) => getOrganisation(id, { signal, ...axiosOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getOrganisation>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetOrganisationQueryResult = NonNullable<
  Awaited<ReturnType<typeof getOrganisation>>
>;
export type GetOrganisationQueryError = AxiosError<OrganisationPayload>;

export function useGetOrganisation<
  TData = Awaited<ReturnType<typeof getOrganisation>>,
  TError = AxiosError<OrganisationPayload>,
>(
  id: number,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOrganisation>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOrganisation>>,
          TError,
          Awaited<ReturnType<typeof getOrganisation>>
        >,
        "initialData"
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetOrganisation<
  TData = Awaited<ReturnType<typeof getOrganisation>>,
  TError = AxiosError<OrganisationPayload>,
>(
  id: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOrganisation>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOrganisation>>,
          TError,
          Awaited<ReturnType<typeof getOrganisation>>
        >,
        "initialData"
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetOrganisation<
  TData = Awaited<ReturnType<typeof getOrganisation>>,
  TError = AxiosError<OrganisationPayload>,
>(
  id: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOrganisation>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get organisation by id
 */

export function useGetOrganisation<
  TData = Awaited<ReturnType<typeof getOrganisation>>,
  TError = AxiosError<OrganisationPayload>,
>(
  id: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOrganisation>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetOrganisationQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Retrieves all members for the given organisation, including their roles and statuses.
 * @summary List organisation members
 */
export const getMembers = (
  organisationId: number,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<OrganisationMemberPayload[]>> => {
  return axios.default.get(
    `https://membership.uibfs.or.ug/uib-api/user/organisation/get-members/${organisationId}`,
    options,
  );
};

export const getGetMembersQueryKey = (organisationId?: number) => {
  return [
    `https://membership.uibfs.or.ug/uib-api/user/organisation/get-members/${organisationId}`,
  ] as const;
};

export const getGetMembersQueryOptions = <
  TData = Awaited<ReturnType<typeof getMembers>>,
  TError = AxiosError<unknown>,
>(
  organisationId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getMembers>>, TError, TData>
    >;
    axios?: AxiosRequestConfig;
  },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetMembersQueryKey(organisationId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getMembers>>> = ({
    signal,
  }) => getMembers(organisationId, { signal, ...axiosOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!organisationId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getMembers>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetMembersQueryResult = NonNullable<
  Awaited<ReturnType<typeof getMembers>>
>;
export type GetMembersQueryError = AxiosError<unknown>;

export function useGetMembers<
  TData = Awaited<ReturnType<typeof getMembers>>,
  TError = AxiosError<unknown>,
>(
  organisationId: number,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getMembers>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMembers>>,
          TError,
          Awaited<ReturnType<typeof getMembers>>
        >,
        "initialData"
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetMembers<
  TData = Awaited<ReturnType<typeof getMembers>>,
  TError = AxiosError<unknown>,
>(
  organisationId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getMembers>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMembers>>,
          TError,
          Awaited<ReturnType<typeof getMembers>>
        >,
        "initialData"
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetMembers<
  TData = Awaited<ReturnType<typeof getMembers>>,
  TError = AxiosError<unknown>,
>(
  organisationId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getMembers>>, TError, TData>
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List organisation members
 */

export function useGetMembers<
  TData = Awaited<ReturnType<typeof getMembers>>,
  TError = AxiosError<unknown>,
>(
  organisationId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getMembers>>, TError, TData>
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetMembersQueryOptions(organisationId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Resolves the organisation associated with the supplied user id (owner or member).
 * @summary Organisation by user id
 */
export const getOrganisationByUserId = (
  userId: number,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<OrganisationPayload>> => {
  return axios.default.get(
    `https://membership.uibfs.or.ug/uib-api/user/organisation/get-by-user/${userId}`,
    options,
  );
};

export const getGetOrganisationByUserIdQueryKey = (userId?: number) => {
  return [
    `https://membership.uibfs.or.ug/uib-api/user/organisation/get-by-user/${userId}`,
  ] as const;
};

export const getGetOrganisationByUserIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getOrganisationByUserId>>,
  TError = AxiosError<OrganisationPayload>,
>(
  userId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOrganisationByUserId>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetOrganisationByUserIdQueryKey(userId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getOrganisationByUserId>>
  > = ({ signal }) =>
    getOrganisationByUserId(userId, { signal, ...axiosOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!userId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getOrganisationByUserId>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetOrganisationByUserIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getOrganisationByUserId>>
>;
export type GetOrganisationByUserIdQueryError = AxiosError<OrganisationPayload>;

export function useGetOrganisationByUserId<
  TData = Awaited<ReturnType<typeof getOrganisationByUserId>>,
  TError = AxiosError<OrganisationPayload>,
>(
  userId: number,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOrganisationByUserId>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOrganisationByUserId>>,
          TError,
          Awaited<ReturnType<typeof getOrganisationByUserId>>
        >,
        "initialData"
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetOrganisationByUserId<
  TData = Awaited<ReturnType<typeof getOrganisationByUserId>>,
  TError = AxiosError<OrganisationPayload>,
>(
  userId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOrganisationByUserId>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOrganisationByUserId>>,
          TError,
          Awaited<ReturnType<typeof getOrganisationByUserId>>
        >,
        "initialData"
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetOrganisationByUserId<
  TData = Awaited<ReturnType<typeof getOrganisationByUserId>>,
  TError = AxiosError<OrganisationPayload>,
>(
  userId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOrganisationByUserId>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Organisation by user id
 */

export function useGetOrganisationByUserId<
  TData = Awaited<ReturnType<typeof getOrganisationByUserId>>,
  TError = AxiosError<OrganisationPayload>,
>(
  userId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOrganisationByUserId>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetOrganisationByUserIdQueryOptions(userId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Returns all organisations visible to the current user (owned or administrated).
 * @summary List organisations for current user
 */
export const getAllOrganisations = (
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<OrganisationPayload[]>> => {
  return axios.default.get(
    `https://membership.uibfs.or.ug/uib-api/user/organisation/get-all`,
    options,
  );
};

export const getGetAllOrganisationsQueryKey = () => {
  return [
    `https://membership.uibfs.or.ug/uib-api/user/organisation/get-all`,
  ] as const;
};

export const getGetAllOrganisationsQueryOptions = <
  TData = Awaited<ReturnType<typeof getAllOrganisations>>,
  TError = AxiosError<unknown>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof getAllOrganisations>>,
      TError,
      TData
    >
  >;
  axios?: AxiosRequestConfig;
}) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAllOrganisationsQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getAllOrganisations>>
  > = ({ signal }) => getAllOrganisations({ signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAllOrganisations>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetAllOrganisationsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAllOrganisations>>
>;
export type GetAllOrganisationsQueryError = AxiosError<unknown>;

export function useGetAllOrganisations<
  TData = Awaited<ReturnType<typeof getAllOrganisations>>,
  TError = AxiosError<unknown>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getAllOrganisations>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllOrganisations>>,
          TError,
          Awaited<ReturnType<typeof getAllOrganisations>>
        >,
        "initialData"
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetAllOrganisations<
  TData = Awaited<ReturnType<typeof getAllOrganisations>>,
  TError = AxiosError<unknown>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getAllOrganisations>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllOrganisations>>,
          TError,
          Awaited<ReturnType<typeof getAllOrganisations>>
        >,
        "initialData"
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetAllOrganisations<
  TData = Awaited<ReturnType<typeof getAllOrganisations>>,
  TError = AxiosError<unknown>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getAllOrganisations>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List organisations for current user
 */

export function useGetAllOrganisations<
  TData = Awaited<ReturnType<typeof getAllOrganisations>>,
  TError = AxiosError<unknown>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getAllOrganisations>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetAllOrganisationsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Returns the profile of the currently authenticated user including roles and organisation assignments.
 * @summary Current authenticated profile
 */
export const getCurentUserDetails = (
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<UserDetailsResponse>> => {
  return axios.default.get(
    `https://membership.uibfs.or.ug/uib-api/user/me`,
    options,
  );
};

export const getGetCurentUserDetailsQueryKey = () => {
  return [`https://membership.uibfs.or.ug/uib-api/user/me`] as const;
};

export const getGetCurentUserDetailsQueryOptions = <
  TData = Awaited<ReturnType<typeof getCurentUserDetails>>,
  TError = AxiosError<unknown>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof getCurentUserDetails>>,
      TError,
      TData
    >
  >;
  axios?: AxiosRequestConfig;
}) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetCurentUserDetailsQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getCurentUserDetails>>
  > = ({ signal }) => getCurentUserDetails({ signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getCurentUserDetails>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetCurentUserDetailsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getCurentUserDetails>>
>;
export type GetCurentUserDetailsQueryError = AxiosError<unknown>;

export function useGetCurentUserDetails<
  TData = Awaited<ReturnType<typeof getCurentUserDetails>>,
  TError = AxiosError<unknown>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getCurentUserDetails>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCurentUserDetails>>,
          TError,
          Awaited<ReturnType<typeof getCurentUserDetails>>
        >,
        "initialData"
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetCurentUserDetails<
  TData = Awaited<ReturnType<typeof getCurentUserDetails>>,
  TError = AxiosError<unknown>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getCurentUserDetails>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCurentUserDetails>>,
          TError,
          Awaited<ReturnType<typeof getCurentUserDetails>>
        >,
        "initialData"
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetCurentUserDetails<
  TData = Awaited<ReturnType<typeof getCurentUserDetails>>,
  TError = AxiosError<unknown>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getCurentUserDetails>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Current authenticated profile
 */

export function useGetCurentUserDetails<
  TData = Awaited<ReturnType<typeof getCurentUserDetails>>,
  TError = AxiosError<unknown>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getCurentUserDetails>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetCurentUserDetailsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Legacy non-paginated endpoint retained for backward compatibility. Prefer the paginated variants.
 * @deprecated
 * @summary List all users (legacy)
 */
export const getAllUsersLegacy = (
  params: GetAllUsersLegacyParams,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<UserDetailsResponse[]>> => {
  return axios.default.get(
    `https://membership.uibfs.or.ug/uib-api/user/get-all-users`,
    {
      ...options,
      params: { ...params, ...options?.params },
    },
  );
};

export const getGetAllUsersLegacyQueryKey = (
  params?: GetAllUsersLegacyParams,
) => {
  return [
    `https://membership.uibfs.or.ug/uib-api/user/get-all-users`,
    ...(params ? [params] : []),
  ] as const;
};

export const getGetAllUsersLegacyQueryOptions = <
  TData = Awaited<ReturnType<typeof getAllUsersLegacy>>,
  TError = AxiosError<unknown>,
>(
  params: GetAllUsersLegacyParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getAllUsersLegacy>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetAllUsersLegacyQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getAllUsersLegacy>>
  > = ({ signal }) => getAllUsersLegacy(params, { signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAllUsersLegacy>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetAllUsersLegacyQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAllUsersLegacy>>
>;
export type GetAllUsersLegacyQueryError = AxiosError<unknown>;

export function useGetAllUsersLegacy<
  TData = Awaited<ReturnType<typeof getAllUsersLegacy>>,
  TError = AxiosError<unknown>,
>(
  params: GetAllUsersLegacyParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getAllUsersLegacy>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllUsersLegacy>>,
          TError,
          Awaited<ReturnType<typeof getAllUsersLegacy>>
        >,
        "initialData"
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetAllUsersLegacy<
  TData = Awaited<ReturnType<typeof getAllUsersLegacy>>,
  TError = AxiosError<unknown>,
>(
  params: GetAllUsersLegacyParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getAllUsersLegacy>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllUsersLegacy>>,
          TError,
          Awaited<ReturnType<typeof getAllUsersLegacy>>
        >,
        "initialData"
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetAllUsersLegacy<
  TData = Awaited<ReturnType<typeof getAllUsersLegacy>>,
  TError = AxiosError<unknown>,
>(
  params: GetAllUsersLegacyParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getAllUsersLegacy>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @deprecated
 * @summary List all users (legacy)
 */

export function useGetAllUsersLegacy<
  TData = Awaited<ReturnType<typeof getAllUsersLegacy>>,
  TError = AxiosError<unknown>,
>(
  params: GetAllUsersLegacyParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getAllUsersLegacy>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetAllUsersLegacyQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Returns a paginated list of users filtered by optional search term and status. Access restricted to administrators.
 * @summary List users (paginated)
 */
export const getAllUsers = (
  params?: GetAllUsersParams,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<Page>> => {
  return axios.default.get(
    `https://membership.uibfs.or.ug/uib-api/user/get-all-users-by-page`,
    {
      ...options,
      params: { ...params, ...options?.params },
    },
  );
};

export const getGetAllUsersQueryKey = (params?: GetAllUsersParams) => {
  return [
    `https://membership.uibfs.or.ug/uib-api/user/get-all-users-by-page`,
    ...(params ? [params] : []),
  ] as const;
};

export const getGetAllUsersQueryOptions = <
  TData = Awaited<ReturnType<typeof getAllUsers>>,
  TError = AxiosError<unknown>,
>(
  params?: GetAllUsersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAllUsers>>, TError, TData>
    >;
    axios?: AxiosRequestConfig;
  },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAllUsersQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAllUsers>>> = ({
    signal,
  }) => getAllUsers(params, { signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAllUsers>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetAllUsersQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAllUsers>>
>;
export type GetAllUsersQueryError = AxiosError<unknown>;

export function useGetAllUsers<
  TData = Awaited<ReturnType<typeof getAllUsers>>,
  TError = AxiosError<unknown>,
>(
  params: undefined | GetAllUsersParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAllUsers>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllUsers>>,
          TError,
          Awaited<ReturnType<typeof getAllUsers>>
        >,
        "initialData"
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetAllUsers<
  TData = Awaited<ReturnType<typeof getAllUsers>>,
  TError = AxiosError<unknown>,
>(
  params?: GetAllUsersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAllUsers>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllUsers>>,
          TError,
          Awaited<ReturnType<typeof getAllUsers>>
        >,
        "initialData"
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetAllUsers<
  TData = Awaited<ReturnType<typeof getAllUsers>>,
  TError = AxiosError<unknown>,
>(
  params?: GetAllUsersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAllUsers>>, TError, TData>
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List users (paginated)
 */

export function useGetAllUsers<
  TData = Awaited<ReturnType<typeof getAllUsers>>,
  TError = AxiosError<unknown>,
>(
  params?: GetAllUsersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAllUsers>>, TError, TData>
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetAllUsersQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Returns the full enumeration of roles available within the platform for assignment.
 * @summary List available roles
 */
export const getAllRoles = (
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<EnumPayload[]>> => {
  return axios.default.get(
    `https://membership.uibfs.or.ug/uib-api/user/get-all-roles`,
    options,
  );
};

export const getGetAllRolesQueryKey = () => {
  return [`https://membership.uibfs.or.ug/uib-api/user/get-all-roles`] as const;
};

export const getGetAllRolesQueryOptions = <
  TData = Awaited<ReturnType<typeof getAllRoles>>,
  TError = AxiosError<unknown>,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof getAllRoles>>, TError, TData>
  >;
  axios?: AxiosRequestConfig;
}) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAllRolesQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAllRoles>>> = ({
    signal,
  }) => getAllRoles({ signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAllRoles>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetAllRolesQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAllRoles>>
>;
export type GetAllRolesQueryError = AxiosError<unknown>;

export function useGetAllRoles<
  TData = Awaited<ReturnType<typeof getAllRoles>>,
  TError = AxiosError<unknown>,
>(
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAllRoles>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllRoles>>,
          TError,
          Awaited<ReturnType<typeof getAllRoles>>
        >,
        "initialData"
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetAllRoles<
  TData = Awaited<ReturnType<typeof getAllRoles>>,
  TError = AxiosError<unknown>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAllRoles>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllRoles>>,
          TError,
          Awaited<ReturnType<typeof getAllRoles>>
        >,
        "initialData"
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetAllRoles<
  TData = Awaited<ReturnType<typeof getAllRoles>>,
  TError = AxiosError<unknown>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAllRoles>>, TError, TData>
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List available roles
 */

export function useGetAllRoles<
  TData = Awaited<ReturnType<typeof getAllRoles>>,
  TError = AxiosError<unknown>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAllRoles>>, TError, TData>
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetAllRolesQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

export const getAllRoles1 = (
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<Role[]>> => {
  return axios.default.get(
    `https://membership.uibfs.or.ug/uib-api/user/enum/roles`,
    options,
  );
};

export const getGetAllRoles1QueryKey = () => {
  return [`https://membership.uibfs.or.ug/uib-api/user/enum/roles`] as const;
};

export const getGetAllRoles1QueryOptions = <
  TData = Awaited<ReturnType<typeof getAllRoles1>>,
  TError = AxiosError<unknown>,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof getAllRoles1>>, TError, TData>
  >;
  axios?: AxiosRequestConfig;
}) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAllRoles1QueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAllRoles1>>> = ({
    signal,
  }) => getAllRoles1({ signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAllRoles1>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetAllRoles1QueryResult = NonNullable<
  Awaited<ReturnType<typeof getAllRoles1>>
>;
export type GetAllRoles1QueryError = AxiosError<unknown>;

export function useGetAllRoles1<
  TData = Awaited<ReturnType<typeof getAllRoles1>>,
  TError = AxiosError<unknown>,
>(
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAllRoles1>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllRoles1>>,
          TError,
          Awaited<ReturnType<typeof getAllRoles1>>
        >,
        "initialData"
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetAllRoles1<
  TData = Awaited<ReturnType<typeof getAllRoles1>>,
  TError = AxiosError<unknown>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAllRoles1>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllRoles1>>,
          TError,
          Awaited<ReturnType<typeof getAllRoles1>>
        >,
        "initialData"
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetAllRoles1<
  TData = Awaited<ReturnType<typeof getAllRoles1>>,
  TError = AxiosError<unknown>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAllRoles1>>, TError, TData>
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };

export function useGetAllRoles1<
  TData = Awaited<ReturnType<typeof getAllRoles1>>,
  TError = AxiosError<unknown>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAllRoles1>>, TError, TData>
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetAllRoles1QueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Redirects the browser to the configured Microsoft Outlook OAuth2 consent page for federated login.
 * @summary Redirect to Outlook OAuth2
 */
export const redirectToOutlook = (
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<unknown>> => {
  return axios.default.get(
    `https://membership.uibfs.or.ug/uib-api/auth/oauth2/outlook`,
    options,
  );
};

export const getRedirectToOutlookQueryKey = () => {
  return [
    `https://membership.uibfs.or.ug/uib-api/auth/oauth2/outlook`,
  ] as const;
};

export const getRedirectToOutlookQueryOptions = <
  TData = Awaited<ReturnType<typeof redirectToOutlook>>,
  TError = AxiosError<null | null>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof redirectToOutlook>>,
      TError,
      TData
    >
  >;
  axios?: AxiosRequestConfig;
}) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getRedirectToOutlookQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof redirectToOutlook>>
  > = ({ signal }) => redirectToOutlook({ signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof redirectToOutlook>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type RedirectToOutlookQueryResult = NonNullable<
  Awaited<ReturnType<typeof redirectToOutlook>>
>;
export type RedirectToOutlookQueryError = AxiosError<null | null>;

export function useRedirectToOutlook<
  TData = Awaited<ReturnType<typeof redirectToOutlook>>,
  TError = AxiosError<null | null>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof redirectToOutlook>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof redirectToOutlook>>,
          TError,
          Awaited<ReturnType<typeof redirectToOutlook>>
        >,
        "initialData"
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useRedirectToOutlook<
  TData = Awaited<ReturnType<typeof redirectToOutlook>>,
  TError = AxiosError<null | null>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof redirectToOutlook>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof redirectToOutlook>>,
          TError,
          Awaited<ReturnType<typeof redirectToOutlook>>
        >,
        "initialData"
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useRedirectToOutlook<
  TData = Awaited<ReturnType<typeof redirectToOutlook>>,
  TError = AxiosError<null | null>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof redirectToOutlook>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Redirect to Outlook OAuth2
 */

export function useRedirectToOutlook<
  TData = Awaited<ReturnType<typeof redirectToOutlook>>,
  TError = AxiosError<null | null>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof redirectToOutlook>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getRedirectToOutlookQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Redirects the browser to the configured Google OAuth2 consent page for federated login.
 * @summary Redirect to Google OAuth2
 */
export const redirectToGoogle = (
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<unknown>> => {
  return axios.default.get(
    `https://membership.uibfs.or.ug/uib-api/auth/oauth2/google`,
    options,
  );
};

export const getRedirectToGoogleQueryKey = () => {
  return [`https://membership.uibfs.or.ug/uib-api/auth/oauth2/google`] as const;
};

export const getRedirectToGoogleQueryOptions = <
  TData = Awaited<ReturnType<typeof redirectToGoogle>>,
  TError = AxiosError<null | null>,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof redirectToGoogle>>, TError, TData>
  >;
  axios?: AxiosRequestConfig;
}) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getRedirectToGoogleQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof redirectToGoogle>>
  > = ({ signal }) => redirectToGoogle({ signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof redirectToGoogle>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type RedirectToGoogleQueryResult = NonNullable<
  Awaited<ReturnType<typeof redirectToGoogle>>
>;
export type RedirectToGoogleQueryError = AxiosError<null | null>;

export function useRedirectToGoogle<
  TData = Awaited<ReturnType<typeof redirectToGoogle>>,
  TError = AxiosError<null | null>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof redirectToGoogle>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof redirectToGoogle>>,
          TError,
          Awaited<ReturnType<typeof redirectToGoogle>>
        >,
        "initialData"
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useRedirectToGoogle<
  TData = Awaited<ReturnType<typeof redirectToGoogle>>,
  TError = AxiosError<null | null>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof redirectToGoogle>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof redirectToGoogle>>,
          TError,
          Awaited<ReturnType<typeof redirectToGoogle>>
        >,
        "initialData"
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useRedirectToGoogle<
  TData = Awaited<ReturnType<typeof redirectToGoogle>>,
  TError = AxiosError<null | null>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof redirectToGoogle>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Redirect to Google OAuth2
 */

export function useRedirectToGoogle<
  TData = Awaited<ReturnType<typeof redirectToGoogle>>,
  TError = AxiosError<null | null>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof redirectToGoogle>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getRedirectToGoogleQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Marks a pending account as active using the token sent to the user during registration.
 * @summary Activate account via token
 */
export const activateUser1 = (
  params: ActivateUser1Params,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<ApiResponse>> => {
  return axios.default.get(
    `https://membership.uibfs.or.ug/uib-api/auth/activate`,
    {
      ...options,
      params: { ...params, ...options?.params },
    },
  );
};

export const getActivateUser1QueryKey = (params?: ActivateUser1Params) => {
  return [
    `https://membership.uibfs.or.ug/uib-api/auth/activate`,
    ...(params ? [params] : []),
  ] as const;
};

export const getActivateUser1QueryOptions = <
  TData = Awaited<ReturnType<typeof activateUser1>>,
  TError = AxiosError<unknown>,
>(
  params: ActivateUser1Params,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof activateUser1>>, TError, TData>
    >;
    axios?: AxiosRequestConfig;
  },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getActivateUser1QueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof activateUser1>>> = ({
    signal,
  }) => activateUser1(params, { signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof activateUser1>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ActivateUser1QueryResult = NonNullable<
  Awaited<ReturnType<typeof activateUser1>>
>;
export type ActivateUser1QueryError = AxiosError<unknown>;

export function useActivateUser1<
  TData = Awaited<ReturnType<typeof activateUser1>>,
  TError = AxiosError<unknown>,
>(
  params: ActivateUser1Params,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof activateUser1>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof activateUser1>>,
          TError,
          Awaited<ReturnType<typeof activateUser1>>
        >,
        "initialData"
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useActivateUser1<
  TData = Awaited<ReturnType<typeof activateUser1>>,
  TError = AxiosError<unknown>,
>(
  params: ActivateUser1Params,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof activateUser1>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof activateUser1>>,
          TError,
          Awaited<ReturnType<typeof activateUser1>>
        >,
        "initialData"
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useActivateUser1<
  TData = Awaited<ReturnType<typeof activateUser1>>,
  TError = AxiosError<unknown>,
>(
  params: ActivateUser1Params,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof activateUser1>>, TError, TData>
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Activate account via token
 */

export function useActivateUser1<
  TData = Awaited<ReturnType<typeof activateUser1>>,
  TError = AxiosError<unknown>,
>(
  params: ActivateUser1Params,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof activateUser1>>, TError, TData>
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getActivateUser1QueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Removes the specified member from the organisation membership list.
 * @summary Remove organisation member
 */
export const removeMember = (
  memberId: number,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<ApiResponse>> => {
  return axios.default.delete(
    `https://membership.uibfs.or.ug/uib-api/user/organisation/remove-member/${memberId}`,
    options,
  );
};

export const getRemoveMemberMutationOptions = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof removeMember>>,
    TError,
    { memberId: number },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof removeMember>>,
  TError,
  { memberId: number },
  TContext
> => {
  const mutationKey = ["removeMember"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof removeMember>>,
    { memberId: number }
  > = (props) => {
    const { memberId } = props ?? {};

    return removeMember(memberId, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type RemoveMemberMutationResult = NonNullable<
  Awaited<ReturnType<typeof removeMember>>
>;

export type RemoveMemberMutationError = AxiosError<unknown>;

/**
 * @summary Remove organisation member
 */
export const useRemoveMember = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof removeMember>>,
      TError,
      { memberId: number },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof removeMember>>,
  TError,
  { memberId: number },
  TContext
> => {
  const mutationOptions = getRemoveMemberMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Soft deletes the organisation identified by the supplied id.
 * @summary Delete organisation
 */
export const deleteOrganisation = (
  id: number,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<ApiResponse>> => {
  return axios.default.delete(
    `https://membership.uibfs.or.ug/uib-api/user/organisation/delete/${id}`,
    options,
  );
};

export const getDeleteOrganisationMutationOptions = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteOrganisation>>,
    TError,
    { id: number },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteOrganisation>>,
  TError,
  { id: number },
  TContext
> => {
  const mutationKey = ["deleteOrganisation"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteOrganisation>>,
    { id: number }
  > = (props) => {
    const { id } = props ?? {};

    return deleteOrganisation(id, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteOrganisationMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteOrganisation>>
>;

export type DeleteOrganisationMutationError = AxiosError<unknown>;

/**
 * @summary Delete organisation
 */
export const useDeleteOrganisation = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteOrganisation>>,
      TError,
      { id: number },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deleteOrganisation>>,
  TError,
  { id: number },
  TContext
> => {
  const mutationOptions = getDeleteOrganisationMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
